// Full-page states. A direct URL to something the user cannot see always lands on one of these,
// never on a blank screen (ADR-0016 §5).
import {
  Button,
  buttonVariants,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  PageHeader as UiPageHeader,
} from "@companyos/ui";
import { Link } from "@tanstack/react-router";
import { CircleAlertIcon, CompassIcon, LockIcon, PackageXIcon } from "lucide-react";
import type { FeatureKey } from "../api/types";

export const ADDON_NAMES: Record<FeatureKey, string> = {
  core: "Core",
  kpi: "KPI",
  memory: "Memory",
  agents: "Agents",
  chat: "Chat",
  time: "Time",
  media: "Media",
  workforce_plugin: "Workforce Control",
};

export function NotEnabledPage({
  what,
  feature,
  companyName,
  slug,
}: {
  what: string;
  feature: FeatureKey;
  companyName: string;
  slug: string;
}) {
  return (
    <Empty data-page="not-enabled">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <PackageXIcon aria-hidden="true" />
        </EmptyMedia>
        <h1 className="font-heading text-lg font-medium">{what} is not enabled</h1>
        <EmptyDescription>
          The {ADDON_NAMES[feature]} add-on is off for {companyName}. Your data is kept; ask a
          company admin or your CompanyOS operator to turn it on.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Link to="/c/$slug" params={{ slug }} className={buttonVariants({ variant: "outline" })}>
          Back to {companyName}
        </Link>
      </EmptyContent>
    </Empty>
  );
}

export function NoAccessPage({ what, slug }: { what: string; slug?: string }) {
  return (
    <Empty data-page="no-access">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <LockIcon aria-hidden="true" />
        </EmptyMedia>
        <h1 className="font-heading text-lg font-medium">{what} is not available to you</h1>
        <EmptyDescription>Your role in this company does not include it.</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        {slug ? (
          <Link to="/c/$slug" params={{ slug }} className={buttonVariants({ variant: "outline" })}>
            Back to the company
          </Link>
        ) : (
          <Link to="/" className={buttonVariants({ variant: "outline" })}>
            Home
          </Link>
        )}
      </EmptyContent>
    </Empty>
  );
}

export function NotFoundPage({ message = "This page does not exist." }: { message?: string }) {
  return (
    <Empty data-page="not-found">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <CompassIcon aria-hidden="true" />
        </EmptyMedia>
        <h1 className="font-heading text-lg font-medium">Not found</h1>
        <EmptyDescription>{message}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Link to="/" className={buttonVariants({ variant: "outline" })}>
          Home
        </Link>
      </EmptyContent>
    </Empty>
  );
}

export function ErrorPage({ error, reset }: { error: unknown; reset?: () => void }) {
  const message = error instanceof Error ? error.message : "Unexpected error.";
  return (
    <Empty data-page="error" role="alert">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <CircleAlertIcon aria-hidden="true" />
        </EmptyMedia>
        <EmptyTitle>Something went wrong</EmptyTitle>
        <EmptyDescription>{message}</EmptyDescription>
      </EmptyHeader>
      {reset ? (
        <EmptyContent>
          <Button variant="outline" onClick={reset}>
            Try again
          </Button>
        </EmptyContent>
      ) : null}
    </Empty>
  );
}

/**
 * Page heading for screens not yet on the `@companyos/ui` PageHeader template: it renders that
 * template, so a title looks the same on every screen while the screens move over.
 */
export function PageHeader({ title, description }: { title: string; description?: string }) {
  return <UiPageHeader title={title} {...(description ? { subtitle: description } : {})} />;
}
